import { NavLink, useNavigate } from "react-router-dom";
import { LayoutGrid, Radio, MessageSquare, MapPin, User, Video } from "lucide-react";
import { NAV } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";
import useUnreadDMs from "@/hooks/useUnreadDMs";
import UnreadDot from "@/components/inbox/UnreadDot";

/**
 * Bottom tab bar — mirrors the mobile app exactly:
 *   Feed · Live · [🎥 elevated Go-Live] · Inbox · Nearby · Profile
 * The center camera button opens the Go-Live modal (not a route).
 */
const leftItems = [
  { to: "/feed", icon: LayoutGrid, label: "Feed", tid: NAV.feedLink },
  { to: "/live", icon: Radio,      label: "Live", tid: NAV.liveLink },
];
const rightItems = [
  { to: "/inbox",    icon: MessageSquare, label: "Inbox",   tid: "nav-inbox-link" },
  { to: "/nearby",   icon: MapPin,        label: "Social Meet",  tid: "nav-nearby-link" },
];

export default function MobileNav({ onCompose, onGoLive }) {
  const centerAction = onGoLive || onCompose;
  const { user } = useAuth();
  const { total: unreadDMs } = useUnreadDMs();
  const navigate = useNavigate();
  const gotoProfile = () => {
    if (user?.handle) navigate(`/u/${user.id}`);
    else navigate("/login");
  };
  return (
    <nav
      className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-black/95 backdrop-blur-md border-t border-white/10"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      data-testid="mobile-tabbar"
    >
      <div className="relative grid grid-cols-6 items-end h-16">
        {leftItems.map(({ to, icon: Icon, label, tid }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={tid}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center py-2 gap-1 text-[10px] uppercase tracking-widest transition-colors ${
                isActive ? "text-rose-500" : "text-zinc-400 hover:text-white"
              }`
            }
          >
            <Icon size={22} strokeWidth={1.6} />
            <span>{label}</span>
          </NavLink>
        ))}

        {/* Center-elevated Go-Live camera button */}
        <div className="flex items-center justify-center">
          <button
            type="button"
            onClick={centerAction}
            data-testid={NAV.composeButton}
            aria-label="Go live"
            className="relative -translate-y-4 w-14 h-14 rounded-full bg-rose-600 hover:bg-rose-500 flex items-center justify-center text-white shadow-[0_0_28px_rgba(225,29,72,0.55)] active:scale-95 transition-transform"
          >
            <Video size={22} strokeWidth={1.8} />
          </button>
        </div>

        {rightItems.map(({ to, icon: Icon, label, tid }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={tid}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center py-2 gap-1 text-[10px] uppercase tracking-widest transition-colors ${
                isActive ? "text-rose-500" : "text-zinc-400 hover:text-white"
              }`
            }
          >
            <span className="relative"><Icon size={22} strokeWidth={1.6} />{to === "/inbox" && <UnreadDot count={unreadDMs} testId="inbox-unread-badge-mobile" />}</span>
            <span>{label}</span>
          </NavLink>
        ))}

        {/* Profile tab uses the current user's handle at click-time */}
        <button
          type="button"
          onClick={gotoProfile}
          data-testid="nav-profile-link"
          className="flex flex-col items-center justify-center py-2 gap-1 text-[10px] uppercase tracking-widest text-zinc-400 hover:text-white transition-colors"
        >
          <User size={22} strokeWidth={1.6} />
          <span>Profile</span>
        </button>
      </div>
    </nav>
  );
}
